/* eslint-disable react-hooks/exhaustive-deps */
/**
 * TriageMode — focused single-asset triage flow for the brand pool.
 *
 * Why this exists: the AssetDetailModal inside BrandAssetGallery requires
 * the operator to decode a checkbox labelled "Visible to renderer / picker"
 * and a Save changes button to express a triage decision. That's the wrong
 * surface for chewing through 50 photos in an hour. This page strips the
 * decision down to two buttons (Keep / Drop) with optional reason on Drop,
 * a plain-English explanation of WHY each photo is in the queue, and a
 * keyboard-driven flow.
 *
 * Backend reuses existing endpoints:
 *   GET  /api/v2/workspaces/{ws}/brand/gallery?classifier_verdict=ambiguous&...
 *   PATCH /api/v2/workspaces/{ws}/brand/assets/{id}     (is_active + reason)
 *
 * Keyboard shortcuts (power mode):
 *   K = Keep (activate or leave activated)
 *   D = Drop (deactivate; prompts for reason)
 *   J = Previous photo
 *   L = Next photo (skip without decision)
 *   S = Skip (alias for L)
 *   ? = Help overlay
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  ArrowLeft,
  ArrowRight,
  ChevronLeft,
  Check,
  X as XIcon,
  Keyboard,
  ImageOff,
  Sparkles,
  ShieldCheck,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { API, authedFetch } from "../components/gallery/utils";
import { reasonCopy, verdictCopy, signalCopy, TRIAGE_ACTIONS } from "../components/gallery/triageCopy";
import { POOLS } from "../components/gallery/utils";

const DEFAULT_WS = "a24c8722-c708-4511-a5cc-8b98a884bbfb";

const TONE_CLASSES = {
  emerald: { ring: "ring-emerald-300", bg: "bg-emerald-50", dot: "bg-emerald-500", text: "text-emerald-800" },
  amber:   { ring: "ring-amber-300",   bg: "bg-amber-50",   dot: "bg-amber-500",   text: "text-amber-800" },
  rose:    { ring: "ring-rose-300",    bg: "bg-rose-50",    dot: "bg-rose-500",    text: "text-rose-800" },
  slate:   { ring: "ring-slate-300",   bg: "bg-slate-50",   dot: "bg-slate-400",   text: "text-slate-700" },
};

const FILTER_PRESETS = {
  ambiguous: { label: "Needs Review", verdict: "ambiguous" },
  unsafe: { label: "Likely Unsafe", verdict: "unsafe" },
  unclassified: { label: "Unclassified", state: "unclassified" },
};


// ─── Keyboard help overlay ──────────────────────────────────────────────


function HelpOverlay({ onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="triage-help-overlay"
    >
      <div
        className="bg-white rounded-lg max-w-md w-full p-6 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Keyboard className="h-5 w-5" /> Keyboard shortcuts
        </h2>
        <table className="w-full text-sm">
          <tbody>
            {[
              ["J", "Previous photo"],
              ["L", "Next photo (skip without deciding)"],
              ["S", "Skip (same as L)"],
              ["?", "Toggle this help"],
              ["Esc", "Close help"],
            ].map(([k, desc]) => (
              <tr key={k} className="border-b border-slate-100 last:border-0">
                <td className="py-2 w-16">
                  <kbd className="px-2 py-0.5 rounded bg-slate-100 border border-slate-300 text-xs font-mono">
                    {k}
                  </kbd>
                </td>
                <td className="py-2 text-slate-700">{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Button onClick={onClose} className="w-full" data-testid="triage-help-close">
          Got it
        </Button>
      </div>
    </div>
  );
}


// ─── Drop reason inline form ────────────────────────────────────────────


function DropReasonForm({ onConfirm, onCancel, saving }) {
  const [reason, setReason] = useState("");
  const submit = () => {
    if (reason.trim().length < 5) {
      toast.warning("Please add a quick reason (at least 5 characters)");
      return;
    }
    onConfirm(reason.trim());
  };
  return (
    <div
      className="bg-rose-50 border border-rose-200 rounded-lg p-4 space-y-2"
      data-testid="triage-drop-reason-form"
    >
      <div className="text-sm font-medium text-rose-900">Why drop this photo?</div>
      <Textarea
        autoFocus
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. low quality, not on-brand, wrong setting"
        className="bg-white"
        rows={2}
        data-testid="triage-drop-reason-input"
      />
      <div className="flex gap-2 justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={saving}
          data-testid="triage-drop-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={submit}
          disabled={saving}
          data-testid="triage-drop-confirm"
        >
          {saving ? (
            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Dropping…</>
          ) : (
            <>Confirm drop</>
          )}
        </Button>
      </div>
    </div>
  );
}


// ─── Signals panel ─ surface what the AI passes saw + disagreed on ────


function SignalsPanel({ signals }) {
  if (!signals || Object.keys(signals).length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 p-3 text-xs text-slate-500 bg-white">
        No signal-level data available for this photo.
      </div>
    );
  }
  // Disagreements first — these are the ones the operator most needs to see.
  const entries = Object.entries(signals).map(([key, sig]) => ({
    key,
    sig,
    disagreed: !!sig?._disagreement,
  }));
  entries.sort((a, b) => Number(b.disagreed) - Number(a.disagreed));

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs space-y-2" data-testid="triage-signals">
      <div className="font-medium text-slate-700 mb-1">What the AI saw</div>
      {entries.map(({ key, sig, disagreed }) => {
        const copy = signalCopy(key);
        const value = sig?.value;
        const valueText = value === true ? copy.yes : value === false ? copy.no : "Uncertain";
        return (
          <div
            key={key}
            className={`flex gap-2 items-start ${disagreed ? "bg-amber-50 border-l-2 border-amber-400 pl-2 -ml-2 py-1 rounded-r" : ""}`}
            data-testid={`triage-signal-${key}`}
          >
            <div className="w-32 flex-shrink-0 text-slate-600">{copy.label}</div>
            <div className="flex-1">
              <div className={disagreed ? "font-medium text-amber-900" : "text-slate-800"}>
                {disagreed ? "⚠ Disagreement — " : ""}{valueText}
              </div>
              {disagreed && sig?.reasoning ? (
                <div className="text-[11px] text-slate-600 mt-1 leading-snug">
                  {sig.reasoning.replace(/^DISAGREEMENT\s*[—-]\s*/i, "")}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ─── Structured action picker — replaces the binary Keep/Drop ─────────


function ActionPicker({ onChoose, saving, defaultPool }) {
  const [chosen, setChosen] = useState(null);
  const [notes, setNotes] = useState("");
  const [poolPick, setPoolPick] = useState(defaultPool || "subjects");

  const tones = {
    emerald: "border-emerald-300 hover:bg-emerald-50 text-emerald-900",
    rose: "border-rose-300 hover:bg-rose-50 text-rose-900",
    amber: "border-amber-300 hover:bg-amber-50 text-amber-900",
    slate: "border-slate-300 hover:bg-slate-50 text-slate-700",
  };

  const submit = () => {
    if (!chosen) return;
    if (chosen.needs_pool_pick && !poolPick) {
      toast.warning("Pick a pool first");
      return;
    }
    onChoose({
      action: chosen,
      notes: notes.trim(),
      newPool: chosen.needs_pool_pick ? poolPick : undefined,
    });
  };

  return (
    <div className="space-y-2" data-testid="triage-action-picker">
      <div className="text-sm font-medium text-slate-700">
        What do you see in this photo?
      </div>
      {TRIAGE_ACTIONS.map((a) => (
        <button
          key={a.code}
          type="button"
          onClick={() => setChosen(a)}
          disabled={saving}
          data-testid={`triage-action-${a.code}`}
          className={`w-full text-left rounded-lg border-2 p-3 transition ${
            chosen?.code === a.code
              ? `${tones[a.tone]} ring-2 ring-offset-1 ring-${a.tone}-400 bg-${a.tone}-50/60`
              : `${tones[a.tone]} bg-white`
          } ${saving ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
        >
          <div className="font-semibold text-sm">{a.label}</div>
          <div className="text-xs text-slate-600 mt-0.5">{a.blurb}</div>
        </button>
      ))}

      {chosen?.needs_pool_pick ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1.5">
          <div className="text-xs font-medium text-amber-900">Move to which pool?</div>
          <select
            value={poolPick}
            onChange={(e) => setPoolPick(e.target.value)}
            className="w-full p-1.5 rounded border border-amber-300 bg-white text-sm"
            data-testid="triage-pool-pick"
          >
            {POOLS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      ) : null}

      {chosen && !chosen.is_skip ? (
        <div>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              chosen.notes_optional
                ? "Optional notes (helps the system learn what you look for)…"
                : "Anything else worth noting? Optional."
            }
            rows={2}
            className="text-sm"
            data-testid="triage-action-notes"
          />
        </div>
      ) : null}

      {chosen ? (
        <Button
          onClick={submit}
          disabled={saving}
          className="w-full h-12"
          data-testid="triage-confirm-action"
        >
          {saving ? (
            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</>
          ) : (
            <>Confirm: {chosen.label}</>
          )}
        </Button>
      ) : null}
    </div>
  );
}


// ─── Main triage page ───────────────────────────────────────────────────


export default function TriageMode() {
  const workspaceId = DEFAULT_WS;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const filterKey = searchParams.get("filter") || "ambiguous";
  const filterPreset = FILTER_PRESETS[filterKey] || FILTER_PRESETS.ambiguous;

  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);
  const [showDropForm, setShowDropForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [counts, setCounts] = useState({ kept: 0, dropped: 0, skipped: 0 });
  const fetchedAtMountRef = useRef(false);

  // Fetch the queue on mount (or filter change)
  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterPreset.verdict) params.set("classifier_verdict", filterPreset.verdict);
      if (filterPreset.state) params.set("classification_state", filterPreset.state);
      params.set("per_page", "200"); // bulk-load — operator works through them in-memory
      const url = `${API}/v2/workspaces/${workspaceId}/brand/gallery?${params.toString()}`;
      const r = await authedFetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setQueue(data.items || []);
      setIndex(0);
    } catch (e) {
      toast.error(`Failed to load queue: ${e.message}`);
      setQueue([]);
    } finally {
      setLoading(false);
    }
  }, [filterKey]);

  useEffect(() => {
    if (!fetchedAtMountRef.current) {
      fetchedAtMountRef.current = true;
      fetchQueue();
    } else {
      fetchQueue();
    }
  }, [fetchQueue]);

  const current = queue[index];
  const total = queue.length;

  // ─── Actions ─────────────────────────────────────────────────────────

  const advance = useCallback(() => {
    setIndex((i) => Math.min(i + 1, queue.length));
  }, [queue.length]);

  const back = useCallback(() => {
    setIndex((i) => Math.max(i - 1, 0));
  }, []);

  const patchAsset = useCallback(
    async (asset_id, payload) => {
      const r = await authedFetch(
        `${API}/v2/workspaces/${workspaceId}/brand/assets/${asset_id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!r.ok) {
        const errBody = await r.text();
        throw new Error(`HTTP ${r.status}: ${errBody.slice(0, 200)}`);
      }
      return r.json();
    },
    [workspaceId]
  );

  const handleAction = useCallback(
    async ({ action, notes, newPool }) => {
      if (!current || saving) return;
      if (action.is_skip) {
        setCounts((c) => ({ ...c, skipped: c.skipped + 1 }));
        advance();
        return;
      }
      setSaving(true);
      try {
        // Structured ground truth into the audit reason. Format is
        // deliberate and machine-parseable: future classifier retraining
        // can group by the [TRIAGE: code] prefix.
        const reasonText =
          `[TRIAGE: ${action.code}] ${action.label}` +
          (notes ? ` — ${notes}` : "");
        const payload = {
          is_active: !!action.is_active,
          reason: reasonText,
        };
        if (newPool && newPool !== current.pool) {
          payload.pool = newPool;
        }
        const result = await patchAsset(current.asset_id, payload);
        // Counts: anything that flips is_active=false counts as "dropped",
        // is_active=true counts as "kept".
        setCounts((c) =>
          action.is_active
            ? { ...c, kept: c.kept + 1 }
            : { ...c, dropped: c.dropped + 1 }
        );
        // Tell the operator whether the action was a real change or a
        // confirmation — both produce audit rows but feel different.
        if (result?.triage_confirmed) {
          toast.success("Confirmed (AI was right) — logged");
        } else if (result?.changed_fields?.length) {
          toast.success(action.is_active ? "Kept (changes saved)" : "Dropped");
        } else {
          toast.success(action.is_active ? "Kept" : "Dropped");
        }
        advance();
      } catch (e) {
        toast.error(`Save failed: ${e.message}`);
      } finally {
        setSaving(false);
      }
    },
    [current, saving, patchAsset, advance]
  );

  const handleSkip = useCallback(() => {
    if (!current) return;
    setCounts((c) => ({ ...c, skipped: c.skipped + 1 }));
    advance();
  }, [current, advance]);

  // ─── Keyboard ────────────────────────────────────────────────────────

  useEffect(() => {
    const onKey = (e) => {
      // Don't capture when typing in inputs
      if (e.target?.tagName === "TEXTAREA" || e.target?.tagName === "INPUT" || e.target?.tagName === "SELECT") return;
      if (helpOpen) {
        if (e.key === "Escape" || e.key === "?") setHelpOpen(false);
        return;
      }
      if (e.key === "j" || e.key === "J") back();
      else if (e.key === "l" || e.key === "L" || e.key === "s" || e.key === "S") handleSkip();
      else if (e.key === "?") setHelpOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [helpOpen, handleSkip, back]);

  // ─── Render ──────────────────────────────────────────────────────────

  const verdict = current?.auto_classification?.verdict || "unclassified";
  const verdictMeta = verdictCopy(verdict);
  const verdictTone = TONE_CLASSES[verdictMeta.tone] || TONE_CLASSES.slate;
  // The publish_safety classifier writes its primary signal to
  // `verdict_reason` (string). Earlier code paths also produced `reasons`
  // (list) and `reason_code` (string); keep all three as fallbacks so this
  // works against any historical row in the pool.
  const primaryReason =
    current?.auto_classification?.verdict_reason ||
    current?.auto_classification?.reasons?.[0] ||
    current?.auto_classification?.reason ||
    current?.auto_classification?.reason_code;
  const reasonMeta = reasonCopy(primaryReason);
  const agreement = current?.auto_classification?._meta?.agreement;

  // The /full endpoint resolves storage_path server-side; it works whether
  // or not the row has a separate thumbnail_path. Use it unconditionally
  // for any asset that exists.
  const thumbUrl = current?.asset_id
    ? `${API}/v2/workspaces/${workspaceId}/brand/assets/${current.asset_id}/full`
    : null;

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-5xl" data-testid="triage-mode-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <Link
            to="/admin/brand-assets"
            className="text-xs text-slate-500 hover:underline inline-flex items-center gap-1"
            data-testid="triage-back-link"
          >
            <ChevronLeft className="h-3 w-3" /> Back to gallery
          </Link>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            Triage: {filterPreset.label}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" data-testid="triage-counts">
            ✓ {counts.kept} · ✗ {counts.dropped} · → {counts.skipped}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setHelpOpen(true)}
            data-testid="triage-help-btn"
          >
            <Keyboard className="h-4 w-4 mr-1" /> Shortcuts
          </Button>
        </div>
      </div>

      {/* Filter selector */}
      <div className="flex gap-2 mb-6 flex-wrap" data-testid="triage-filter-row">
        {Object.entries(FILTER_PRESETS).map(([k, p]) => (
          <Button
            key={k}
            variant={filterKey === k ? "default" : "outline"}
            size="sm"
            onClick={() => setSearchParams({ filter: k })}
            data-testid={`triage-filter-${k}`}
          >
            {p.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="aspect-video w-full rounded-lg" />
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : queue.length === 0 ? (
        <div
          className="border border-dashed border-slate-300 rounded-lg p-12 text-center"
          data-testid="triage-empty"
        >
          <Check className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
          <h2 className="text-lg font-semibold">All clear</h2>
          <p className="text-sm text-slate-500 mb-4">
            No photos in the {filterPreset.label.toLowerCase()} queue.
          </p>
          <Link to="/admin/brand-assets">
            <Button variant="outline" data-testid="triage-empty-back">
              Back to gallery
            </Button>
          </Link>
        </div>
      ) : index >= queue.length ? (
        <div
          className="border border-dashed border-slate-300 rounded-lg p-12 text-center"
          data-testid="triage-session-done"
        >
          <Check className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
          <h2 className="text-lg font-semibold">Session complete</h2>
          <p className="text-sm text-slate-700 mb-2">
            You worked through {queue.length} photo{queue.length === 1 ? "" : "s"}.
          </p>
          <p className="text-sm text-slate-500 mb-4">
            ✓ Kept {counts.kept} · ✗ Dropped {counts.dropped} · → Skipped {counts.skipped}
          </p>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={fetchQueue} data-testid="triage-session-reload">
              Reload queue
            </Button>
            <Link to="/admin/brand-assets">
              <Button data-testid="triage-session-back">Back to gallery</Button>
            </Link>
          </div>
        </div>
      ) : current ? (
        <>
          {/* Progress chip */}
          <div className="text-xs text-slate-500 mb-3" data-testid="triage-progress">
            Photo {index + 1} of {total}
            <span className="ml-2 text-slate-400">·</span>
            <span className="ml-2 font-mono">{current.asset_id?.slice(0, 8)}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6">
            {/* Photo */}
            <div
              className="relative bg-slate-100 rounded-lg overflow-hidden flex items-center justify-center min-h-[300px]"
              data-testid="triage-photo"
            >
              {thumbUrl ? (
                <img
                  src={thumbUrl}
                  alt={current.filename || current.asset_id}
                  className="max-w-full max-h-[70vh] object-contain"
                  onError={(e) => {
                    e.currentTarget.replaceWith(
                      Object.assign(document.createElement("div"), {
                        className: "flex flex-col items-center justify-center text-slate-400 p-8",
                        innerHTML: '<div>Image failed to load</div>',
                      })
                    );
                  }}
                />
              ) : (
                <div className="text-slate-400">
                  <ImageOff className="h-12 w-12" />
                </div>
              )}
            </div>

            {/* Decision panel */}
            <div className="space-y-4">
              {/* Verdict + reason in plain English */}
              <div
                className={`rounded-lg border p-4 ${verdictTone.bg} ${verdictTone.text}`}
                data-testid="triage-verdict-card"
              >
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide mb-2">
                  <span className={`h-2 w-2 rounded-full ${verdictTone.dot}`} />
                  {verdictMeta.headline}
                </div>
                <div className="font-semibold mb-1" data-testid="triage-reason-headline">
                  {reasonMeta.headline}
                </div>
                <div className="text-sm leading-relaxed">{reasonMeta.blurb}</div>
                {agreement === "disagree" ? (
                  <div className="mt-2 text-xs flex items-center gap-1 text-amber-700">
                    <AlertTriangle className="h-3 w-3" /> The two AI passes reached different verdicts.
                  </div>
                ) : null}
              </div>

              {/* What the AI saw — surface signals with disagreement highlighted */}
              <SignalsPanel signals={current?.auto_classification?.signals} />

              {/* File metadata mini-block */}
              <div
                className="rounded-lg border border-slate-200 p-3 text-xs space-y-1 bg-white"
                data-testid="triage-meta"
              >
                <div className="font-medium text-slate-700 mb-1 truncate">
                  {current.filename || "(no filename)"}
                </div>
                {current?.auto_classification?.tags?.description ? (
                  <div className="text-slate-600 italic mb-1">
                    "{current.auto_classification.tags.description}"
                  </div>
                ) : null}
                <div className="flex gap-x-3 gap-y-1 flex-wrap text-slate-500">
                  <span>Origin: <b className="text-slate-700">{current.origin || "—"}</b></span>
                  <span>Pool: <b className="text-slate-700">{current.pool || "—"}</b></span>
                  <span>
                    Active:{" "}
                    <b className={current.is_active ? "text-emerald-700" : "text-slate-600"}>
                      {current.is_active ? "yes" : "no"}
                    </b>
                  </span>
                </div>
                {current.source_folder ? (
                  <div className="text-slate-500 truncate">
                    Folder: <span className="text-slate-700">{current.source_folder}</span>
                  </div>
                ) : null}
                {current.asset_metadata?.consent_status ? (
                  <div className="flex items-center gap-1 text-slate-500">
                    <ShieldCheck className="h-3 w-3" /> Consent:{" "}
                    <b className="text-slate-700">{current.asset_metadata.consent_status}</b>
                  </div>
                ) : null}
              </div>

              {/* Structured action picker — operator picks a labelled
                  category which becomes the ground-truth signal for the
                  audit log + future classifier training. */}
              <ActionPicker
                saving={saving}
                defaultPool={current.pool}
                onChoose={handleAction}
              />

              {/* Navigation row */}
              <div className="flex justify-between items-center text-sm">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={back}
                  disabled={index === 0 || saving}
                  data-testid="triage-prev-btn"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" /> Previous
                  <kbd className="ml-2 px-1 py-0.5 bg-slate-100 rounded text-[10px] font-mono">J</kbd>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSkip}
                  disabled={saving}
                  data-testid="triage-skip-btn"
                >
                  Skip <ArrowRight className="h-4 w-4 ml-1" />
                  <kbd className="ml-2 px-1 py-0.5 bg-slate-100 rounded text-[10px] font-mono">L</kbd>
                </Button>
              </div>
            </div>
          </div>
        </>
      ) : null}

      {helpOpen ? <HelpOverlay onClose={() => setHelpOpen(false)} /> : null}
    </div>
  );
}
